import React from 'react';
import { Box, Typography, Button, Card } from '@mui/joy';
import { Link, useLocation } from 'react-router-dom';
import { Home, ErrorOutline } from '@mui/icons-material';

interface NotFoundProps {
  /** Optional custom message (e.g. for published form when form/transaction not found) */
  message?: string;
  /** Whether to show the "Go to Home" link (false when on published/public page) */
  showHomeLink?: boolean;
}

const PUBLISH_PATH_PREFIX = (process.env.REACT_APP_PUBLISH_PATH_PREFIX || 'pago').replace(/^\/+|\/+$/g, '') || 'pago';

const NotFound: React.FC<NotFoundProps> = ({
  message = 'Page not found',
  showHomeLink = true,
}) => {
  const location = useLocation();
  const isPublishedPath = location.pathname.startsWith(`/${PUBLISH_PATH_PREFIX}/`);

  const displayMessage = message;
  const displayShowHomeLink = showHomeLink ?? !isPublishedPath;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        bgcolor: 'background.body',
        backgroundImage: 'radial-gradient(ellipse at 50% 0%, var(--joy-palette-primary-500Channel) / 0.08, transparent 50%)',
      }}
    >
      <Card
        variant="outlined"
        sx={{
          maxWidth: 420,
          width: '100%',
          textAlign: 'center',
          boxShadow: 'sm',
          borderRadius: 'lg',
          p: 4,
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            bgcolor: 'danger.softBg',
            color: 'danger.plainColor',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2,
          }}
        >
          <ErrorOutline sx={{ fontSize: 40 }} />
        </Box>
        <Typography
          level="h1"
          sx={{
            fontSize: '4rem',
            fontWeight: 700,
            lineHeight: 1,
            color: 'text.tertiary',
            mb: 1,
            letterSpacing: '-0.02em',
          }}
        >
          404
        </Typography>
        <Typography level="title-lg" sx={{ mb: 0.5 }}>
          {displayMessage}
        </Typography>
        <Typography level="body-md" sx={{ color: 'text.secondary', mb: 3 }}>
          The resource you are looking for does not exist or is no longer available.
        </Typography>
        {displayShowHomeLink && (
          <Button
            component={Link}
            to="/"
            variant="soft"
            color="neutral"
            startDecorator={<Home />}
            sx={{ alignSelf: 'center' }}
          >
            Go to Home
          </Button>
        )}
      </Card>
    </Box>
  );
};

export default NotFound;
